import { Autocomplete, Grid, Skeleton, TextField } from '@mui/material';
import { type HTMLAttributes, useEffect, useRef, useState } from 'react';

import { useIntersectionObserver } from '@/Utility/hooks';

import * as data from './googleFonts.json';

const fontsArray = ['Arial', 'system-ui', 'sans-serif', 'monospace', ...data.fonts];

type OptionType = {
  option: string;
} & HTMLAttributes<HTMLLIElement>;

const loadFont = (font: string, cb: VoidFunction) => {
  const link = document.createElement('link') as HTMLLinkElement;
  const href = `https://fonts.googleapis.com/css?family=${font}&subset=latin,latin-ext`;
  link.setAttribute('href', href);
  link.setAttribute('rel', 'stylesheet');
  link.setAttribute('type', 'text/css');
  link.onload = cb;

  document.head.appendChild(link);
};

const OptionItem = ({ option, ...props }: OptionType) => {
  const [fontLoaded, setFontLoaded] = useState<boolean>(false);

  const elRef = useRef<HTMLLIElement>(null);
  const observer = useIntersectionObserver(elRef, {
    threshold: 0,
    rootMargin: '1000px',
    freezeOnceVisible: false,
    root: elRef.current?.parentElement,
  });

  useEffect(() => {
    // Don't attempt to load system fontd
    if (!data.fonts.includes(option)) setFontLoaded(true);
    else if (observer?.isIntersecting && !fontLoaded) {
      loadFont(option, () => {
        setFontLoaded(true);
      });
    }
  }, [observer]);

  return (
    <li ref={elRef} {...props} style={{ fontFamily: option }}>
      <Grid container alignItems="center">
        {fontLoaded ? (
          <Grid size={{ xs: 12 }}>{option}</Grid>
        ) : (
          <Skeleton variant="rounded" width={'100%'} height={25} />
        )}
      </Grid>
    </li>
  );
};

type Props = {
  font: string;
  onChange: (value: string | null) => void;
  onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

const FontPicker = ({ font, onChange, onBlur }: Props) => {
  return (
    <Autocomplete
      autoComplete
      id="font-auto"
      includeInputInList
      filterSelectedOptions
      value={font}
      fullWidth
      options={fontsArray}
      onChange={(event, value) => {
        onChange(value);
      }}
      renderInput={(params) => (
        <TextField {...params} label="Type and select a font" style={{ fontFamily: font }} onBlur={onBlur} />
      )}
      renderOption={(props, option) => <OptionItem {...props} option={option} />}
    />
  );
};

export type { Props as FontPickerProps };
export default FontPicker;
